import React, { useMemo } from 'react';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { P } from '@frontend/shadcn/components/typography/p';
import { Button } from '@frontend/shadcn/components/ui/button';
import { ArrowRightIcon } from '@revfluence/fresh-icons/regular/esm';
import { useGetSpecificationTemplateByNameQuery, useGetAllWorkletsQuery, useGetResources } from '@frontend/app/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faCircleInfo, faListCheck } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@frontend/shadcn/lib/utils';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@frontend/shadcn/components/ui/hover-card';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { ClientFeature } from '@frontend/app/constants';
import { useAppContext } from '@frontend/context/AppContext';
import { CAMPAIGN_DESCRIPTION, CAMPAIGN_TO_TEMPLATE_MAP, TEMPLATE_TO_CAMPAIGN_MAP } from '../constants';
import { useCampaign } from '../CampaignContext';
import { getCampaignBgColor, getCampaignIconV2, getSpecIconAndBgColor, getCampaignIconName } from '../utils';
import { WorkletSpecKey } from '../../utils';

// Constants
const CARD_STYLES = {
  base: 'bg-white border border-[#E5E7EB] rounded-2xl',
  shadow: 'shadow-[0_2px_6px_-4px_rgba(0,0,0,0.12),0_4px_8px_rgba(59,69,71,0.08),0_8px_24px_rgba(59,69,71,0.05)]',
  transition: 'transition-opacity duration-300 ease-in-out',
};

const LOADING_COLORS = {
  base: 'bg-gray-200',
  icon: 'bg-[#55B6B3]',
  button: 'bg-[#162428]',
};

// Types
interface WorkflowStepProps {
  title: string;
  specURI: string;
}

interface CardHeaderProps {
  title: string;
}

interface LoadingSkeletonProps {
  className?: string;
}

// Components
const CardHeader: React.FC<CardHeaderProps> = ({ title }) => {
  const campaignName = TEMPLATE_TO_CAMPAIGN_MAP[title] || title;
  const name = CAMPAIGN_TO_TEMPLATE_MAP[title] || title;
  return (
    <div className="flex items-center gap-2">
      <div className={cn('w-6 h-6 rounded-md flex items-center justify-center', getCampaignBgColor(name))}>
        <div className="text-white flex items-center justify-center">{getCampaignIconV2(name, 12)}</div>
      </div>
      <H3 className="text-grey-6 text-sm font-medium leading-none m-0 p-0">{campaignName}</H3>
    </div>
  );
};

const WorkflowStep: React.FC<WorkflowStepProps> = ({ title, specURI }) => {
  const { icon, bgColor } = getSpecIconAndBgColor(specURI, 12);
  return (
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 rounded flex items-center justify-center ${bgColor} text-white`}>{icon}</div>
      <P className="text-sm text-grey-6 m-0">{title}</P>
    </div>
  );
};

const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({ className }) => (
  <Card className={cn(CARD_STYLES.base, CARD_STYLES.shadow, className)} data-testid="loading-skeleton">
    <CardContent className="p-4">
      <div className="flex flex-col gap-4 animate-pulse">
        <div className="flex items-center gap-3">
          <div className={cn('w-6 h-6 rounded-md', LOADING_COLORS.base)} />
          <div className={cn('h-4 w-32 rounded', LOADING_COLORS.base)} />
        </div>

        <div className="space-y-2">
          <div className={cn('h-4 rounded w-3/4', LOADING_COLORS.base)} />
          <div className={cn('h-4 rounded w-1/2', LOADING_COLORS.base)} />
        </div>

        <div className="flex flex-col gap-3">
          <div className={cn('h-5 rounded w-32', LOADING_COLORS.base)} />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={cn('w-8 h-8 rounded', LOADING_COLORS.base)} />
                <div className={cn('h-4 rounded w-40', LOADING_COLORS.base)} />
              </div>
            ))}
          </div>
        </div>

        <div className={cn('w-full h-9 rounded-lg', LOADING_COLORS.base)} />
      </div>
    </CardContent>
  </Card>
);

const CampaignCard: React.FC<{
  templateName: string;
  campaignDescription: string;
  worklets: Array<{ specKey?: string; specLogoURL?: string; specTitle: string; specURI: string }>;
  onSelectTemplate: () => void;
}> = ({ templateName, campaignDescription, worklets, onSelectTemplate }) => (
  <Card className={cn(CARD_STYLES.base, CARD_STYLES.shadow, CARD_STYLES.transition)}>
    <CardContent className="p-4">
      <div className="flex flex-col gap-4">
        <CardHeader title={templateName} />

        <P className="text-[#5D6770] text-sm leading-5">{campaignDescription}</P>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faListCheck} />
            <H3 className="text-sm font-medium text-grey-6 m-0">Default Workflow</H3>
            <HoverCard>
              <HoverCardTrigger asChild>
                <FontAwesomeIcon icon={faCircleInfo} className="text-grey-3 cursor-pointer" />
              </HoverCardTrigger>
              <HoverCardContent className="w-80">
                <div className="flex flex-col gap-1">
                  <h4 className="font-medium">Default Workflow</h4>
                  <p className="text-sm text-muted-foreground">
                    Launch your campaign with default workflow stages. Workflow can be configured and customized after the campaign is created.
                  </p>
                </div>
              </HoverCardContent>
            </HoverCard>
          </div>
          <div className="flex flex-col gap-2">
            {worklets.map((worklet, index) => (
              <WorkflowStep key={worklet.specKey || index} title={worklet.specTitle} specURI={worklet.specURI} />
            ))}
          </div>
        </div>

        <Button
          variant="default"
          className={cn('w-[160px] h-9 text-white rounded-lg hover:bg-[#162428]/90', LOADING_COLORS.button)}
          onClick={onSelectTemplate}
        >
          <div className="flex items-center justify-center gap-2">
            Select Template
            <ArrowRightIcon className="w-4 h-4" />
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

export const CampaignVariantsSnapshot: React.FC = () => {
  const { templateName, setTemplateDetails, setCurrentStep, setCampaignIcon, campaignIcon, campaignImage } = useCampaign();

  const { [ClientFeature.PFA_V2]: isPfaV2Enabled } = useClientFeatures();

  const apiTemplateName = templateName && templateName && CAMPAIGN_TO_TEMPLATE_MAP[templateName] ? CAMPAIGN_TO_TEMPLATE_MAP[templateName] : undefined;
  const campaignDescription = templateName && CAMPAIGN_DESCRIPTION[templateName] ? CAMPAIGN_DESCRIPTION[templateName] : '';

  const { data: templateData, loading: templateLoading } = useGetSpecificationTemplateByNameQuery({
    variables: { templateName: apiTemplateName },
    skip: !apiTemplateName,
  });

  const { apolloClient } = useAppContext();
  const {
    activeResources,
  } = useGetResources({
    variables: {
      resourceType: 'shopify',
      ignoreRequestContextUserId: true,
    },
    client: apolloClient,
  });

  const isShopifyConnected = activeResources?.length > 0;

  const loadedTemplate = useMemo(() => {
    const result = templateData?.template;
    const workletSpecKeys = result?.workletSpecKeys;

    if (!workletSpecKeys) return result;

    // Clone the array to avoid mutating the original
    const updatedWorkletSpecKeys = [...workletSpecKeys];

    const cpsIndex = updatedWorkletSpecKeys.indexOf(WorkletSpecKey.PFACreatorProductSelection);
    const promoAndLinksIndex = updatedWorkletSpecKeys.indexOf(WorkletSpecKey.STASalesLinksAndPromoCodes);

    // If neither index is found, return the original result
    if (cpsIndex === -1 && promoAndLinksIndex === -1) return result;

    // Shopify not connected
    if (!isShopifyConnected) {
      if (cpsIndex !== -1) {
        updatedWorkletSpecKeys.splice(cpsIndex, 1, WorkletSpecKey.PFAOfflineCreatorProductSelection);
      }
      if (promoAndLinksIndex !== -1) {
        updatedWorkletSpecKeys.splice(promoAndLinksIndex, 1, WorkletSpecKey.STASalesLinks);
      }
    } else if (promoAndLinksIndex !== -1) {
      updatedWorkletSpecKeys.splice(promoAndLinksIndex, 1, WorkletSpecKey.STAPromoCodes);
    }

    // PFA V2 enabled (and Shopify connected)
    if (isPfaV2Enabled && isShopifyConnected && cpsIndex !== -1) {
      updatedWorkletSpecKeys.splice(cpsIndex, 1, WorkletSpecKey.PFACreatorProductSelectionV2);
    }

    // Add more conditions here as needed

    return {
      ...result,
      workletSpecKeys: updatedWorkletSpecKeys,
    };
  }, [templateData, isPfaV2Enabled, isShopifyConnected]);

  const { data: workletData, loading: workletLoading } = useGetAllWorkletsQuery({
    variables: {
      specKeys: loadedTemplate?.workletSpecKeys || [],
    },
    skip: !loadedTemplate?.workletSpecKeys?.length,
  });

  const worklets = useMemo(() => workletData?.worklets || [], [workletData]);

  const handleSelectTemplate = () => {
    setTemplateDetails(templateName, worklets);
    setCurrentStep(2);
    if (!campaignImage && !campaignIcon) {
      setCampaignIcon(getCampaignIconName(templateName));
    }
  };

  const isLoading = templateLoading || workletLoading;

  return (
    <div className="p-6 h-full max-w-[375px] mx-auto">
      <div className="sticky top-0 flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <div className="bg-grey-6 rounded-lg p-2 h-6 w-6 flex items-center justify-center">
            <FontAwesomeIcon icon={faBolt} className="text-white" />
          </div>
          <H3 className="font-medium p-0 m-0">Campaign Snapshot</H3>
        </div>

        {!templateName ? (
          <P className="text-grey-5 text-sm leading-5">
            Quick information about your campaign will show up here. You can always customize your campaign further
            after it's created.
          </P>
        ) : isLoading ? (
          <LoadingSkeleton />
        ) : (
          <CampaignCard
            templateName={templateName}
            campaignDescription={campaignDescription}
            worklets={worklets}
            onSelectTemplate={handleSelectTemplate}
          />
        )}
      </div>
    </div>
  );
};
